import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLatest, useMountedState, useUpdateEffect } from 'react-use';
import { yupResolver } from '@hookform/resolvers/yup';
import { DialogContent, FormControl, Stack, Typography } from '@mui/material';

import { DrugsApi, type DrugFormData } from '@/api/domains/drugs.api';
import { DrugsQueryKeys } from '@/api/domains/drugs.query-keys';
import type { DrugURLResources } from '@/api/domains/drugs.types';
import { OpenLinkButton } from '@/components/buttons/OpenLinkButton';
import { useGlobalErrorStore } from '@/components/dialogs';
import { CustomFormLabel, FormProvider } from '@/components/form-controls';
import { ModalBody } from '@/components/Modal/ModalBody2';
import { NomenclatureConfirmationEditDialog } from '@/components/nomenclature/dialogs/NomenclatureConfirmationEditDialog';
import {
  useNomenclatureContextWindows,
  useNomenclatureStoreActions,
  useNomenclatureStorePreferredTab,
} from '@/components/nomenclature/useNomenclatureStore';
import type { RecordStatusId } from '@/shared/constants/record-status.constants';
import { RECORD_STATUS_ACTIVE } from '@/shared/constants/record-status.constants';
import { Yup } from '@/shared/lib/yup';
import type { AfterSubmit } from '@/shared/types';

import { nomenclatureCreateEvent } from '../../events';
import type { NomenclatureFormProps } from '../NomenclatureForm';
import { NomenclatureModalSidebar } from '../NomenclatureModalSidebar';
import type { NomenclatureModalVariant } from '../types';
import { LinkField } from './components';

type LinksFormProps = {
  onClose: VoidFunction;
  drug: NomenclatureFormProps['drug'];
  variant: NomenclatureModalVariant | null;
};

type LinksFormFields = AfterSubmit &
  DrugURLResources & {
    record_status_id?: RecordStatusId;
  };

export type LinksFormFieldName = keyof DrugURLResources;

type LinksFields = { name: LinksFormFieldName; label: string };

const formSchema = Yup.object().shape({
  liki: Yup.string().trim(),
  aptekauz: Yup.string().trim(),
  onlinepharm: Yup.string().trim(),
  milliykatalogi: Yup.string().trim(),
  dorigar: Yup.string().trim(),
  otherSources: Yup.string().trim(),
});

const urlFields: LinksFields[] = [
  { name: 'milliykatalogi', label: 'milliykatalogi.uz' },
  { name: 'dorigar', label: 'dorigar.uz' },
  { name: 'liki', label: 'liki.uz' },
  { name: 'aptekauz', label: 'apteka.uz' },
  { name: 'onlinepharm', label: 'onlinepharm.uz' },
];

const selfTabName = 'additionalCodesAndLinks';

export const LinksForm = (props: LinksFormProps) => {
  const { onClose, drug, variant } = props;
  const { t } = useTranslation('nomenclature-page');

  const isMounted = useMountedState();
  const { setGlobalError } = useGlobalErrorStore();
  const queryClient = useQueryClient();
  const preferredTab = useNomenclatureStorePreferredTab();
  const { isConfirmationCreateDialogVisible, isConfirmationEditDialogVisible } = useNomenclatureContextWindows();
  const {
    closeConfirmationCreateDialog,
    closeConfirmationEditDialog,
    openConfirmationEditDialog,
    setActiveTab,
    setSelectedRow,
  } = useNomenclatureStoreActions();

  const isEditMode = variant === 'edit';
  const isEditAfterCreationMode = variant === 'editAfterCreation';

  const methods = useForm<LinksFormFields>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      liki: drug?.url_resources?.liki ?? '',
      aptekauz: drug?.url_resources?.aptekauz ?? '',
      onlinepharm: drug?.url_resources?.onlinepharm ?? '',
      milliykatalogi: drug?.url_resources?.milliykatalogi ?? '',
      dorigar: drug?.url_resources?.dorigar ?? '',
      otherSources: drug?.url_resources?.otherSources ?? '',

      record_status_id: drug?.record_status_id ?? RECORD_STATUS_ACTIVE,
    },
  });

  const {
    watch,
    handleSubmit: wrapHandleSubmit,
    formState: { isDirty, isSubmitting },
  } = methods;

  const latestFormState = useLatest({ isDirty });

  useUpdateEffect(() => {
    if (preferredTab === selfTabName) return;

    if (latestFormState.current.isDirty) {
      openConfirmationEditDialog();
    } else {
      setActiveTab(preferredTab);
    }
  }, [preferredTab]);

  const handleSubmit = async (formData: LinksFormFields) => {
    try {
      const { record_status_id, ...urls } = formData;

      if (drug?.maker && isDirty) {
        const preparedFormData: DrugFormData = {
          name: drug.name,
          category_id: drug.category_id,
          maker_id: drug.maker_id,
          measure_unit_id: drug.measure_unit_id,
          vat_id: drug.vat_id,
          maker_name: drug.maker.name,
          country_name: drug.maker.country,
          record_status_id,
          url_resources: urls,
        };

        const updatedDrug = await DrugsApi.updateDrug(drug.id, preparedFormData);
        queryClient.invalidateQueries(DrugsQueryKeys.refreshCurrentSearch());
        setSelectedRow(updatedDrug);
      }

      if (isEditAfterCreationMode && preferredTab === selfTabName && drug) {
        nomenclatureCreateEvent.emit(drug);
      }

      if (isConfirmationCreateDialogVisible) closeConfirmationCreateDialog();
      if (isConfirmationEditDialogVisible) closeConfirmationEditDialog();

      if (preferredTab !== selfTabName) setActiveTab(preferredTab);
      if (preferredTab === selfTabName) onClose();
    } catch (error) {
      setGlobalError(error);

      if (isMounted()) {
        // @ts-expect-error - error object type issue
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  const handleNomenclatureConfirmationEditDialogClose = () => {
    if (preferredTab === selfTabName) {
      onClose();
    } else {
      setActiveTab(preferredTab);
    }
  };

  const values = watch();

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit}>
      <ModalBody
        sidebar={
          <NomenclatureModalSidebar drug={drug} isEditMode={isEditMode || isEditAfterCreationMode} onCancel={onClose} />
        }
      >
        <DialogContent sx={{ padding: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('modals.form.ui.links.title')}
          </Typography>

          <Stack spacing={3}>
            {urlFields.map(({ name, label }) => (
              <FormControl fullWidth key={name}>
                <Stack spacing={0.5}>
                  <CustomFormLabel htmlFor={name}>{label}</CustomFormLabel>
                  <Stack direction="row" spacing={2}>
                    <LinkField name={name} />
                    <OpenLinkButton url={values[name]} />
                  </Stack>
                </Stack>
              </FormControl>
            ))}

            <Stack spacing={0.5}>
              <CustomFormLabel htmlFor="otherSources">{t('modals.form.ui.links.other-sources')}</CustomFormLabel>

              <LinkField
                name="otherSources"
                multiline
                minRows={2}
                maxRows={4}
                sx={{
                  paddingRight: '129px',
                  '& ::-webkit-scrollbar': {
                    width: '12px',
                  },
                  '& ::-webkit-scrollbar-thumb': {
                    backgroundColor: '#9AA7B5',
                    border: '4px solid transparent',
                    backgroundClip: 'padding-box',
                    borderRadius: '9px',
                  },
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>
      </ModalBody>

      <NomenclatureConfirmationEditDialog
        isLoading={isSubmitting}
        onProceed={wrapHandleSubmit(handleSubmit)}
        onClose={handleNomenclatureConfirmationEditDialogClose}
      />
    </FormProvider>
  );
};
